import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authPost } from "../../lib";

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 1100, width: "100%", margin: "0 auto", padding: theme.spacing(3) },
  buttonRow: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1.5), marginBottom: theme.spacing(2) },
  btn: { textTransform: "none", minWidth: 200 },
  summary: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 4,
    marginBottom: theme.spacing(2),
    backgroundColor: "#e8f5f0",
    color: "#155724",
    fontWeight: 500,
  },
  errorBox: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 4,
    marginBottom: theme.spacing(2),
    backgroundColor: "#f8d7da",
    color: "#721c24",
    fontWeight: 500,
  },
  infoBox: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 4,
    marginBottom: theme.spacing(2),
    backgroundColor: "#e3f2fd",
    color: "#0d47a1",
    fontWeight: 500,
  },
  processed: { marginBottom: theme.spacing(1.5), color: "#555" },
  statusChip: { fontWeight: 600, fontSize: "0.72rem" },
}));

// The three actions the legacy PHP page exposed. create_offers writes live
// marketplace listings, so it gets a confirm gate; the other two are safe.
const ACTIONS = [
  { action: "create_offers", label: "Import Watches to Watchfinder", color: "primary" },
  { action: "update_stock",  label: "Update Watch Stock",           color: "primary" },
  // Hidden until the price sync is verified — the backend still accepts
  // "update_prices", so restoring the button is enough to re-enable it.
  // { action: "update_prices", label: "Update Prices",                color: "primary" },
];

// A successful run with no summary and no actionable log means the PHP side
// found nothing flagged / everything already current.
function nothingHappened(result) {
  if (!result || !result.ok || result.summary || result.error) return false;
  const meaningful = result.log.filter(
    (e) => e && e.status && !/^info$/i.test(e.status)
  );
  return meaningful.length === 0;
}

// Colour-code by status prefix. "Skipped - Category Not Found" is AMBER, not
// red — it's a normal outcome (model not in Watchfinder's catalog, routinely
// 20-40% of a batch) and must not read as a failure.
function statusStyle(status) {
  const s = String(status || "");
  if (s.startsWith("Success")) return { backgroundColor: "#e8f5e9", color: "#2e7d32" };
  if (s.startsWith("Skipped")) return { backgroundColor: "#fff3e0", color: "#e65100" };
  if (s.startsWith("Error"))   return { backgroundColor: "#ffebee", color: "#c62828" };
  return { backgroundColor: "#eceff1", color: "#455a64" }; // Info / neutral
}

export function WatchfinderSyncPage() {
  const classes = useStyles();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function runAction(action) {
    setBusy(true);
    setResult(null);

    const res = await authPost("/watchfinder/sync", { action });

    // On a non-2xx, the auth layer moves the response body onto res.error and
    // blanks res.data. The PHP endpoint returns a useful `log` even on 500, so
    // read the payload from whichever side carries it.
    const errored = !!(res && res.error && Object.keys(res.error).length);
    const payload = errored ? res.error : res.data;

    setBusy(false);
    setResult({
      ok: !errored,
      summary: payload?.summary || null,
      stats: payload?.stats || null,
      error: payload?.error || (errored ? payload?.message : null),
      log: Array.isArray(payload?.log) ? payload.log : [],
    });
  }

  return (
    <Box className={classes.root}>
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="h6">Watchfinder Sync</Typography>
        {busy && <CircularProgress size={20} style={{ marginLeft: 12 }} />}
      </Box>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
        Pushes pre-owned watches to the Watchfinder (Mirakl) marketplace. Import can take up
        to ~90 seconds — leave the page open until it returns.
      </Typography>

      <Box className={classes.buttonRow}>
        {ACTIONS.map((a) => (
          <Button
            key={a.action}
            variant="contained"
            color={a.color}
            className={classes.btn}
            disabled={busy}
            onClick={() => runAction(a.action)}
          >
            {a.label}
          </Button>
        ))}
      </Box>

      {result?.summary && <div className={classes.summary}>{result.summary}</div>}
      {result?.error && <div className={classes.errorBox}>{result.error}</div>}
      {nothingHappened(result) && (
        <div className={classes.infoBox}>
          Nothing to sync — no watches were flagged for this action. Everything is already up to date on Watchfinder.
        </div>
      )}

      {result?.stats && (result.stats.new_products != null || result.stats.total != null) && (
        <Typography variant="body2" className={classes.processed}>
          Processed: {result.stats.new_products ?? 0} / {result.stats.total ?? 0}
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product ID</TableCell>
              <TableCell>Model Number</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result?.log?.length ? (
              result.log.map((entry, i) => (
                <TableRow key={i} hover>
                  <TableCell>{entry.productId}</TableCell>
                  <TableCell>{entry.sku}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={entry.status}
                      className={classes.statusChip}
                      style={statusStyle(entry.status)}
                    />
                  </TableCell>
                  <TableCell>{entry.detail}</TableCell>
                  <TableCell>{entry.timestamp}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ color: "#999", padding: 24 }}>
                  {busy
                    ? "Running…"
                    : result
                    ? "No per-watch detail for this run."
                    : "Run a sync to view the log."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
